// ============================================================================
// /hours/:token — PUBLIC hours report (Decision 2, 2026-08-28).
//
// The facility admin audits their hours SELF-SERVE, on THEIR phone or
// computer, from a link HR emailed them — they will not ask HR, they have no
// TCS account, and they will almost never stand at a kiosk. The same page
// serves a sub owner auditing THEIR hourly employees (provider scope).
//
// The token is the whole identity: an unguessable 48-hex-char string, checked
// by rpc_client_hours_report ONLY against live, unexpired, unrevoked links,
// with every open logged. Invalid token → a calm dead-end revealing nothing.
//
// ⛔ HOURS ONLY. No rate, amount, cost or margin exists in the payload (the
//    RPC guarantees it; the smoke asserts it) and none may be rendered here.
//
// The RPC returns ONE ROW PER SHIFT (190 days), with its clock times, plus the
// real semimonthly pay-period boundaries; every horizon — last 24h, week, pay
// period, month, trends across periods — and both readings — BY DAY and BY
// PERSON — are computed HERE from that one payload, so switching costs no
// second request and the two readings cannot disagree about a total.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../shared/supabase';
import type { HoursReportPayload, HoursReportPeriod, HoursReportRow } from './types';
import { formatSiteTime } from '../../lib/siteTime';

type Horizon = 'day' | 'week' | 'period' | 'month' | 'trends';

/** The same shifts, read two ways. Never two queries — see `detailRows`. */
type Reading = 'by_day' | 'by_person';

const HORIZONS: Array<{ id: Horizon; label: string }> = [
  { id: 'day', label: 'Last 24 h' },
  { id: 'week', label: 'Last 7 days' },
  { id: 'period', label: 'This pay period' },
  { id: 'month', label: 'This month' },
  { id: 'trends', label: 'Trends' },
];

/** "2026-08-27" (a DATE from the DB) → local Date at midnight, no TZ slip. */
function dateOf(d: string): Date {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
}

function fmtDay(d: string): string {
  return dateOf(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** A clock time on the WORKER's clock — the building's market — labelled
 *  ("7:52 PM CT"), whoever opens the link and from wherever (Ben, 2026-09-24).
 *  Never a money-shaped thing: `site_tz` is a time zone name and nothing else. */
function fmtClock(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  return formatSiteTime(iso, tz);
}

function fmtRange(a: string, b: string): string {
  const f = (s: string) => dateOf(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${f(a)} – ${f(b)}`;
}

function inRange(row: HoursReportRow, from: Date, to: Date): boolean {
  const d = dateOf(row.shift_date);
  return d >= from && d <= to;
}

export function HoursReportPage() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<'loading' | 'invalid' | 'ready'>('loading');
  const [payload, setPayload] = useState<HoursReportPayload | null>(null);
  const [horizon, setHorizon] = useState<Horizon>('day');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [workerFilter, setWorkerFilter] = useState('');
  // ⛔ The two readings of the same shifts. "By day" answers "who was in my
  // building on Wednesday"; "by person" answers "what did she work this
  // period". The facility manager wants the first and a sub lead auditing her
  // own crew usually wants the second.
  const [reading, setReading] = useState<Reading>('by_day');
  const [day, setDay] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!token) { setPhase('invalid'); return; }
      const { data, error } = await supabase.rpc('rpc_client_hours_report', { p_token: token });
      if (cancelled) return;
      const p = data as HoursReportPayload | null;
      if (error || !p || p.ok !== true) { setPhase('invalid'); return; }
      setPayload(p);
      setPhase('ready');
    })();
    return () => { cancelled = true; };
  }, [token]);

  const rows = useMemo(() => payload?.rows ?? [], [payload]);
  const periods = useMemo(() => payload?.periods ?? [], [payload]);

  const facilities = useMemo(
    () => Array.from(new Set(rows.map((r) => r.facility))).sort(),
    [rows],
  );
  const workers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.worker_name))).sort(),
    [rows],
  );

  const filtered = useMemo(
    () => rows.filter((r) =>
      (facilityFilter === '' || r.facility === facilityFilter) &&
      (workerFilter === '' || r.worker_name === workerFilter)),
    [rows, facilityFilter, workerFilter],
  );

  // The pay period containing today (or the most recent one that started).
  const currentPeriod: HoursReportPeriod | null = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (const p of periods) {
      if (dateOf(p.start) <= today && dateOf(p.end) >= today) return p;
    }
    return periods[0] ?? null;
  }, [periods]);

  const horizonRows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (horizon === 'day') {
      const from = new Date(today);
      from.setDate(from.getDate() - 1); // daily grain: today + yesterday covers the last 24h
      return filtered.filter((r) => inRange(r, from, today));
    }
    if (horizon === 'week') {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return filtered.filter((r) => inRange(r, from, today));
    }
    if (horizon === 'period') {
      if (!currentPeriod) return [];
      return filtered.filter((r) => inRange(r, dateOf(currentPeriod.start), dateOf(currentPeriod.end)));
    }
    if (horizon === 'month') {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return filtered.filter((r) => inRange(r, from, today));
    }
    return filtered; // trends uses everything, grouped by period below
  }, [filtered, horizon, currentPeriod]);

  const totalHours = useMemo(
    () => horizonRows.reduce((s, r) => s + (r.hours ?? 0), 0),
    [horizonRows],
  );

  const byWorker = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of horizonRows) m.set(r.worker_name, (m.get(r.worker_name) ?? 0) + (r.hours ?? 0));
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [horizonRows]);

  /**
   * ⛔ BOTH READINGS COME OFF THE SAME `horizonRows`, so their totals cannot
   * disagree — there is one set of shifts and two ways of sorting it, not two
   * queries that could drift. The smoke asserts the two totals are equal.
   */
  const days = useMemo(
    () => Array.from(new Set(horizonRows.map((r) => r.shift_date))).sort((a, b) => b.localeCompare(a)),
    [horizonRows],
  );

  // The chosen day stays chosen while it still exists in the window; otherwise
  // it falls back to the most recent day rather than rendering empty.
  const shownDay = useMemo(
    () => (day && days.includes(day) ? day : days[0] ?? null),
    [day, days],
  );

  const detailRows = useMemo(() => {
    if (reading === 'by_day') {
      return horizonRows
        .filter((r) => r.shift_date === shownDay)
        .sort((a, b) => (a.in_at ?? '').localeCompare(b.in_at ?? '') || a.worker_name.localeCompare(b.worker_name));
    }
    return [...horizonRows].sort((a, b) =>
      a.worker_name.localeCompare(b.worker_name)
      || b.shift_date.localeCompare(a.shift_date)
      || (a.in_at ?? '').localeCompare(b.in_at ?? ''));
  }, [horizonRows, reading, shownDay]);

  /** What the chosen reading is actually totalling, said out loud. */
  const readingHours = useMemo(
    () => detailRows.reduce((s, r) => s + (r.hours ?? 0), 0),
    [detailRows],
  );

  /** Trends: total hours per pay period, oldest → newest, only periods that
   *  have ended or started (the returned window is ~6 months ≈ 12 periods). */
  const trendData = useMemo(() => {
    const out: Array<{ period: HoursReportPeriod; hours: number }> = [];
    for (const p of [...periods].sort((a, b) => a.start.localeCompare(b.start))) {
      const from = dateOf(p.start);
      const to = dateOf(p.end);
      const hours = filtered.reduce((s, r) => s + (inRange(r, from, to) ? (r.hours ?? 0) : 0), 0);
      out.push({ period: p, hours });
    }
    // Drop leading empty periods (before the data window opened).
    const firstWithData = out.findIndex((x) => x.hours > 0);
    return firstWithData === -1 ? [] : out.slice(firstWithData);
  }, [periods, filtered]);

  const trendMax = useMemo(() => Math.max(1, ...trendData.map((t) => t.hours)), [trendData]);

  if (phase === 'loading') {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-tcs-text-secondary">
        Loading your hours…
      </div>
    );
  }

  if (phase === 'invalid' || !payload) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-2 px-6 text-center">
        <div className="text-lg font-bold text-tcs-text">This link isn't active</div>
        <div className="text-sm text-tcs-text-secondary">
          It may have expired or been replaced. Reach out to your Terminal Cleaning Services contact
          for a fresh one.
        </div>
      </div>
    );
  }

  const scopeTitle = (payload.scope_display ?? []).join(' · ');

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-tcs-text-muted">
        Terminal Cleaning Services · hours report
      </div>
      <h1 className="mt-1 text-xl font-bold text-tcs-text">{scopeTitle || 'Hours'}</h1>
      <div className="mt-1 text-xs text-tcs-text-secondary">
        Prepared for {payload.prepared_for}
        {payload.generated_at && ` · as of ${new Date(payload.generated_at).toLocaleString(undefined, {
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        })}`}
        {' · hours only — live snapshot, refresh any time'}
      </div>

      {/* Horizon switcher */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {HORIZONS.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => setHorizon(h.id)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${horizon === h.id
              ? 'bg-tcs-inverse text-tcs-inverse-ink'
              : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}
          >
            {h.label}
          </button>
        ))}
      </div>

      {/* Filters — only where there is something to filter */}
      {(facilities.length > 1 || workers.length > 1) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {facilities.length > 1 && (
            <select value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)}
              className="h-9 max-w-[260px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text"
              aria-label="Filter by facility">
              <option value="">All facilities</option>
              {facilities.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          )}
          {workers.length > 1 && (
            <select value={workerFilter} onChange={(e) => setWorkerFilter(e.target.value)}
              className="h-9 max-w-[220px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text"
              aria-label="Filter by worker">
              <option value="">All workers</option>
              {workers.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          )}
        </div>
      )}

      {horizon !== 'trends' ? (
        <>
          <div className="mt-4 rounded-2xl border border-tcs-border bg-tcs-raised p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              {horizon === 'period' && currentPeriod
                ? `Pay period ${fmtRange(currentPeriod.start, currentPeriod.end)}`
                : HORIZONS.find((h) => h.id === horizon)?.label}
            </div>
            <div className="mt-1 text-3xl font-bold tabular-nums text-tcs-text">{totalHours.toFixed(2)} h</div>
            <div className="mt-0.5 text-xs text-tcs-text-secondary">
              {byWorker.length} worker{byWorker.length === 1 ? '' : 's'} · {horizonRows.length} shift-day
              {horizonRows.length === 1 ? '' : 's'}
            </div>
            {byWorker.length > 0 && (
              <div className="mt-3 flex flex-col gap-1">
                {byWorker.map(([name, hours]) => (
                  <div key={name} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-tcs-text">{name}</span>
                    <span className="tabular-nums font-semibold text-tcs-text">{hours.toFixed(2)} h</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* By day / by person — the same shifts, sorted for the question
              being asked. ⛔ No money column exists on this page to switch on. */}
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            {([['by_day', 'By day'], ['by_person', 'By person']] as const).map(([id, label]) => (
              <button key={id} type="button" onClick={() => setReading(id)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${reading === id
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
                {label}
              </button>
            ))}
            {reading === 'by_day' && days.length > 0 && (
              <select value={shownDay ?? ''} onChange={(e) => setDay(e.target.value)}
                aria-label="Which day"
                className="h-8 rounded-full border border-tcs-border-strong bg-tcs-raised px-3 text-xs font-semibold text-tcs-text">
                {days.map((d) => <option key={d} value={d}>{fmtDay(d)}</option>)}
              </select>
            )}
            <span className="text-[11px] text-tcs-text-muted">
              {reading === 'by_day'
                ? `${detailRows.length} shift${detailRows.length === 1 ? '' : 's'} · ${readingHours.toFixed(2)} h that day`
                : `${byWorker.length} ${byWorker.length === 1 ? 'person' : 'people'} · ${readingHours.toFixed(2)} h in this window`}
            </span>
          </div>

          <div className="mt-2 overflow-x-auto rounded-xl border border-tcs-border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                  {reading === 'by_person' && <th className="px-3 py-2 font-semibold">Person</th>}
                  <th className="px-3 py-2 font-semibold">{reading === 'by_day' ? 'Person' : 'Day'}</th>
                  <th className="px-3 py-2 font-semibold">Where</th>
                  <th className="px-3 py-2 font-semibold">In (worker&rsquo;s time)</th>
                  <th className="px-3 py-2 font-semibold">Out (worker&rsquo;s time)</th>
                  <th className="px-3 py-2 text-right font-semibold">Hours</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((r, i) => (
                  <tr key={`${r.shift_date}-${r.worker_name}-${r.facility}-${r.service_line}-${i}`}
                    className="border-t border-tcs-border bg-tcs-raised">
                    {reading === 'by_person' && (
                      <td className="px-3 py-2 font-medium text-tcs-text">
                        {/* Named once per run of their own rows, so a person's
                            week reads as one block rather than a repeated name. */}
                        {i === 0 || detailRows[i - 1].worker_name !== r.worker_name ? r.worker_name : ''}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {reading === 'by_day'
                        ? <span className="font-medium text-tcs-text">{r.worker_name}</span>
                        : fmtDay(r.shift_date)}
                    </td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{r.facility}</td>
                    <td className="px-3 py-2 tabular-nums text-tcs-text-secondary">{fmtClock(r.in_at, r.site_tz)}</td>
                    <td className="px-3 py-2 tabular-nums text-tcs-text-secondary">{fmtClock(r.out_at, r.site_tz)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                  </tr>
                ))}
                {detailRows.length === 0 && (
                  <tr>
                    <td colSpan={reading === 'by_person' ? 6 : 5} className="px-3 py-8 text-center text-sm text-tcs-text-muted">
                      No hours recorded in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="mt-4 rounded-2xl border border-tcs-border bg-tcs-raised p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
            Hours by pay period
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {trendData.map(({ period, hours }) => (
              <div key={period.start}>
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-tcs-text-secondary">{fmtRange(period.start, period.end)}</span>
                  <span className="tabular-nums font-semibold text-tcs-text">{hours.toFixed(2)} h</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-tcs-surface">
                  <div
                    className="h-full rounded-full bg-tcs-inverse"
                    style={{ width: `${Math.max(2, Math.round((hours / trendMax) * 100))}%` }}
                  />
                </div>
              </div>
            ))}
            {trendData.length === 0 && (
              <div className="py-6 text-center text-sm text-tcs-text-muted">
                Not enough history yet — trends fill in as pay periods close.
              </div>
            )}
          </div>
          <div className="mt-3 text-[11px] text-tcs-text-muted">
            Pay periods are semimonthly (1st–15th, 16th–end). The current period is partial until it closes.
          </div>
        </div>
      )}

      <div className="mt-6 text-center text-[11px] text-tcs-text-muted">
        Questions about anything here? Reply to the email this link arrived in.
      </div>
    </div>
  );
}
